import { Search } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../../global.css";
import { bankingPartnerAPI } from "../../../services/BankingPartnerPortal/api";
import usePieChart from "../../../hooks/usePieChart";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BankingPartnerTable = () => {
  const navigate = useNavigate();
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [bankingPartners, setBankingPartners] = useState([]);
  const [segmentBreakdownData, setSegmentBreakdownData] = useState([]);
  const [segmentLoading, setSegmentLoading] = useState(true);
  const [segmentError, setSegmentError] = useState(null);

  useEffect(() => {
    fetchBankingPartners();
    fetchSegmentBreakdown();
  }, []);

  const fetchSegmentBreakdown = async () => {
    setSegmentLoading(true);
    setSegmentError(null);
    try {
      const res = await bankingPartnerAPI.getSegmentBreakdown();
      if (res.success) {
        setSegmentBreakdownData(res.data || []);
      } else {
        setSegmentError("Failed to fetch segment breakdown");
      }
    } catch (err) {
      setSegmentError(err.message || "Error fetching segment breakdown");
    } finally {
      setSegmentLoading(false);
    }
  };

  const fetchBankingPartners = async () => {
    try {
      const response = await bankingPartnerAPI.getAll();
      localStorage.setItem("bankingPartnerId", response.data[0]._id);
      if (response.success !== true) {
        throw new Error("Failed to fetch banking partners");
      }
      const transformedData = response.data.map((partner) => ({
        name: partner.bankName,
        category: partner?.bankCategory,
        assignedFiles: partner?.assignedFiles || "0",
        processedFiles: partner?.processedFiles || "0",
        pendingFiles: partner?.pendingFiles || "0",
        rejectedFiles: partner?.rejectedFiles || "0",
        approvalRate: partner?.processingRate || partner?.approvalRate || "0%",
        processingTime: partner?.processingTime || "0 days",
        subText: partner?.branchName,
        id: partner._id,
        status: partner.status,
        representative: partner.representative,
        loanManagement: partner.loanManagement,
        compliance: partner.compliance,
        branchIFSCCode: partner.branchIFSCCode,
        bankAddress: partner.bankAddress,
      }));

      setBankingPartners(transformedData);
    } catch (err) {
      setSegmentError(err.message);
    }
  };

  const handleViewPartner = (partnerIndex) => {
    navigate(`${partnerIndex}`);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectedAll;
    setSelectedAll(newSelectAll);
    if (newSelectAll) {
      setSelectedRows(bankingPartners.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const handleCheckboxChange = (index) => {
    const updatedSelection = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    setSelectedRows(updatedSelection);
    setSelectedAll(updatedSelection.length === bankingPartners.length);
  };

  const filteredPartners = bankingPartners.filter((partner) =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ Export to Excel and ZIP
  const exportToExcelAndZip = async () => {
    const partnersToExport =
      selectedRows.length > 0
        ? bankingPartners.filter((_, index) => selectedRows.includes(index))
        : bankingPartners;

    if (!partnersToExport || partnersToExport.length === 0) {
      toast.warn("No banking partners to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = partnersToExport.map((p) => ({
      "Bank Name": p.name,
      Category: p.category,
      "Branch Name": p.subText || "N/A",
      "Assigned Files": p.assignedFiles,
      "Processed Files": p.processedFiles,
      "Pending Files": p.pendingFiles,
      "Rejected Files": p.rejectedFiles,
      "Approval Rate": p.approvalRate,
      "Processing Time": p.processingTime,
      Status: p.status,
      Representative: p.representative || "N/A",
      "Loan Management": p.loanManagement || "N/A",
      Compliance: p.compliance || "N/A",
      "Branch IFSC Code": p.branchIFSCCode || "N/A",
      "Bank Address": p.bankAddress || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "BankingPartners");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("banking_partners.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "banking_partners.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const SegmentBreakdownPieChart = usePieChart({
    data: segmentBreakdownData,
    colorRange: [45, 60], // green shades
    heading: "Segment Breakdown",
    defaultPeriod: "all",
    loading: segmentLoading,
  });

  return (
    <div className="pl-4 md:pl-6 mr-5">
      {/* Segment Breakdown Chart */}
      <div className="flex justify-center items-center gap-4 w-full mt-6 mb-6">
        <div style={{ width: "100%" }}>{SegmentBreakdownPieChart}</div>
      </div>

      {/* Search + Download */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
        <div className="relative w-full md:w-64 my-2 text-customWhite">
          <div className="relative flex">
            <Search className="absolute left-3 top-2 pr-2 w-5 h-5 md:w-6 md:h-6 text-white" />
            <input
              type="text"
              placeholder="Search by Bank Name"
              className="bg-transparent border border-[#333333] rounded-3xl text-white w-full md:min-w-72 pl-10 pr-3 py-2 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={exportToExcelAndZip}
          className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
        >
          Download as ZIP
        </button>
      </div>
      <div className="text-sm text-[#F3F3F3] mb-4">
        Select to enable bulk options
        <span className="ml-2 text-sm text-blue-400">
          ({selectedRows.length} selected)
        </span>
      </div>

      {/* Table */}
      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full">
          <thead className="border-y border-[#333333]">
            <tr className="text-left text-xs md:text-sm">
              <th className="p-2 md:p-4 border border-[#333333]">
                <input
                  type="checkbox"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                  className="appearance-none h-4 w-4 md:h-5 md:w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                />
              </th>
              <th className="p-2 md:p-4 border border-[#333333]">BANK NAME</th>
              <th className="p-2 md:p-4 border border-[#333333]">CATEGORY</th>
              <th className="p-2 md:p-4 border border-[#333333]">
                ASSIGNED FILES
              </th>
              <th className="p-2 md:p-4 border border-[#333333]">
                PROCESSED FILES
              </th>
              <th className="p-2 md:p-4 border border-[#333333]">
                PENDING FILES
              </th>
              <th className="p-2 md:p-4 border border-[#333333]">
                REJECTED FILES
              </th>
              <th className="p-2 md:p-4 border border-[#333333] w-[150px] md:w-[250px]">
                APPROVAL RATE AND AVG PROCESSING TIME
              </th>
              <th className="p-2 md:p-4 border border-[#333333]">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="text-xs md:text-sm">
            {filteredPartners.length === 0 ? (
              <tr>
                <td
                  colSpan="9"
                  className="p-3 md:p-6 text-center text-gray-400"
                >
                  No rows found
                </td>
              </tr>
            ) : (
              filteredPartners.map((partner, index) => (
                <tr key={index} className="border-b border-[#333333]">
                  <td className="p-2 md:p-4 border border-[#333333]">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(index)}
                      onChange={() => handleCheckboxChange(index)}
                      className="appearance-none h-4 w-4 md:h-5 md:w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    />
                  </td>
                  <td className="p-2 md:p-4 border capitalize border-[#333333]">
                    <div>
                      {partner.name}
                      {partner.subText && (
                        <div className="text-xs text-gray-400">
                          {partner.subText}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    {partner.category}
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    {partner.assignedFiles}
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    {partner.processedFiles}
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    {partner.pendingFiles}
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    {partner.rejectedFiles}
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs md:text-sm ${
                          parseInt(partner.approvalRate) >= 85
                            ? "text-green-500"
                            : parseInt(partner.approvalRate) >= 70
                            ? "text-customYellow"
                            : "text-red-500"
                        }`}
                      >
                        {partner.approvalRate}
                      </span>
                      <span className="text-xs md:text-sm text-gray-400">
                        {partner.processingTime}
                      </span>
                    </div>
                  </td>
                  <td className="p-2 md:p-4 border border-[#333333]">
                    <button
                      className="text-customYellow bg-transparent underline text-xs md:text-sm"
                      onClick={() => handleViewPartner(partner.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankingPartnerTable;
